import type { Scte35Marker, Id3TagInfo } from "../types";

export function parseCueOutDuration(value: string): number | undefined {
  const num = parseFloat(value.trim());
  if (!Number.isNaN(num)) return num;
  const match =
    value.match(/(?:^|[,\s])DURATION\s*=\s*([0-9.]+)/i) ||
    value.match(/Duration\s*=\s*([0-9.]+)/i);
  if (match && match[1] != null) {
    const dStr = match[1];
    const d = parseFloat(dStr);
    return Number.isNaN(d) ? undefined : d;
  }
  return undefined;
}

export function parseCueOutCont(
  value: string
): { elapsed?: number; duration?: number } | undefined {
  const res: { elapsed?: number; duration?: number } = {};

  const elapsedMatch = value.match(/Elapsed\s*=\s*([0-9.]+)/i);
  const durationMatch = value.match(/Duration\s*=\s*([0-9.]+)/i);
  if (elapsedMatch && elapsedMatch[1] != null) {
    const e = parseFloat(elapsedMatch[1]);
    if (!Number.isNaN(e)) res.elapsed = e;
  }
  if (durationMatch && durationMatch[1] != null) {
    const d = parseFloat(durationMatch[1]);
    if (!Number.isNaN(d)) res.duration = d;
  }

  if (!("elapsed" in res) || !("duration" in res)) {
    const slashMatch = value.match(/([0-9.]+)\s*\/\s*([0-9.]+)/);
    if (slashMatch && slashMatch[1] && slashMatch[2]) {
      const elapsed = parseFloat(slashMatch[1]);
      const duration = parseFloat(slashMatch[2]);
      if (!Number.isNaN(elapsed) && !("elapsed" in res)) res.elapsed = elapsed;
      if (!Number.isNaN(duration) && !("duration" in res)) res.duration = duration;
    }
  }

  if ("elapsed" in res || "duration" in res) return res;
  return undefined;
}

export function parseAttributeList(value: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const regex = /([A-Z0-9-]+)=(("[^"]*")|([^",]*))(?:,|$)/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(value)) !== null) {
    const key: string = (match[1] ?? "") as string;
    let rawVal: string = (match[3] ?? match[4] ?? "") as string;
    if (rawVal.startsWith('"') && rawVal.endsWith('"')) {
      rawVal = rawVal.slice(1, -1);
    }
    if (key) {
      attrs[key] = rawVal;
    }
  }
  return attrs;
}

export function toNumber(val: unknown): number | undefined {
  if (val == null) return undefined;
  const n = typeof val === "string" ? parseFloat(val) : Number(val);
  return Number.isNaN(n) ? undefined : n;
}

export function isManifestBasedMarker(marker: Scte35Marker): boolean {
  const raw = marker.raw as any;
  if (!raw) return false;

  if (raw.tag) {
    const tag = String(raw.tag);
    return (
      tag.includes("EXT-X-CUE-OUT") ||
      tag.includes("EXT-X-CUE-IN") ||
      tag.includes("EXT-X-DATERANGE")
    );
  }

  if (raw.id3) return false;

  if (raw.splice_command_type) return false;

  return false;
}

export function decodeId3ValueToText(value: string | Uint8Array): string | undefined {
  try {
    if (typeof value === "string") return value;
    const decoder = new TextDecoder("utf-8", { fatal: false });
    const text = decoder.decode(value);
    if (text && /[\x20-\x7E]/.test(text)) return text;
    let out = "";
    for (let i = 0; i < value.length; i++)
      out += String.fromCharCode(value[i]!);
    return out;
  } catch {
    return undefined;
  }
}

export function getFragmentStartSeconds(frag: any): number | undefined {
  const candidates = [frag?.start, frag?.startPTS];
  for (const candidate of candidates) {
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return candidate;
    }
  }
  return undefined;
}

export function parseScte35FromId3(tag: Id3TagInfo): Scte35Marker | undefined {
  const text = decodeId3ValueToText(tag.value);
  if (!text) return undefined;

  const cueOutMatch =
    text.match(/EXT-X-CUE-OUT(?::([^\r\n]*))?/i) ||
    text.match(/CUE-OUT(?::([^\r\n]*))?/i);
  if (cueOutMatch) {
    const arg = (cueOutMatch[1] ?? "").trim();
    const dur = parseCueOutDuration(arg);
    const marker: Scte35Marker = {
      type: "start",
      ...(tag.ptsSeconds !== undefined ? { ptsSeconds: tag.ptsSeconds } : {}),
      ...(dur !== undefined ? { durationSeconds: dur } : {}),
      raw: { id3: text },
    } as Scte35Marker;
    return marker;
  }

  const cueOutContMatch = text.match(/EXT-X-CUE-OUT-CONT:([^\r\n]*)/i);
  if (cueOutContMatch) {
    const arg = (cueOutContMatch[1] ?? "").trim();
    const cont = parseCueOutCont(arg);
    const marker: Scte35Marker = {
      type: "progress",
      ...(tag.ptsSeconds !== undefined ? { ptsSeconds: tag.ptsSeconds } : {}),
      ...(cont?.duration !== undefined
        ? { durationSeconds: cont.duration }
        : {}),
      raw: { id3: text },
    } as Scte35Marker;
    return marker;
  }

  const cueInMatch = text.match(/EXT-X-CUE-IN\b/i) || text.match(/CUE-IN\b/i);
  if (cueInMatch) {
    const marker: Scte35Marker = {
      type: "end",
      ...(tag.ptsSeconds !== undefined ? { ptsSeconds: tag.ptsSeconds } : {}),
      raw: { id3: text },
    } as Scte35Marker;
    return marker;
  }

  const daterangeMatch = text.match(/EXT-X-DATERANGE:([^\r\n]*)/i);
  if (daterangeMatch) {
    const attrs = parseAttributeList(daterangeMatch[1] ?? "");
    const hasScteOut =
      "SCTE35-OUT" in attrs || attrs["SCTE35-OUT"] !== undefined;
    const hasScteIn =
      "SCTE35-IN" in attrs || attrs["SCTE35-IN"] !== undefined;
    const klass = String(attrs["CLASS"] ?? "");
    const duration = toNumber(attrs["DURATION"]);
    if (hasScteOut || /com\.apple\.hls\.cue/i.test(klass)) {
      const marker: Scte35Marker = {
        type: "start",
        ...(tag.ptsSeconds !== undefined
          ? { ptsSeconds: tag.ptsSeconds }
          : {}),
        ...(duration !== undefined ? { durationSeconds: duration } : {}),
        raw: { id3: text, attrs },
      } as Scte35Marker;
      return marker;
    }
    if (hasScteIn) {
      const marker: Scte35Marker = {
        type: "end",
        ...(tag.ptsSeconds !== undefined
          ? { ptsSeconds: tag.ptsSeconds }
          : {}),
        raw: { id3: text, attrs },
      } as Scte35Marker;
      return marker;
    }
  }

  if (/SCTE35-OUT/i.test(text)) {
    const marker: Scte35Marker = {
      type: "start",
      ...(tag.ptsSeconds !== undefined ? { ptsSeconds: tag.ptsSeconds } : {}),
      raw: { id3: text },
    } as Scte35Marker;
    return marker;
  }
  if (/SCTE35-IN/i.test(text)) {
    const marker: Scte35Marker = {
      type: "end",
      ...(tag.ptsSeconds !== undefined ? { ptsSeconds: tag.ptsSeconds } : {}),
      raw: { id3: text },
    } as Scte35Marker;
    return marker;
  }

  if (tag.value instanceof Uint8Array) {
    const bin = parseScte35Binary(tag.value);
    if (bin) return bin;
  }

  return undefined;
}

export function normalizeFragmentPayload(payload: unknown): Uint8Array | undefined {
  if (payload instanceof Uint8Array) {
    return payload;
  }
  if (payload instanceof ArrayBuffer) {
    return new Uint8Array(payload);
  }
  if (ArrayBuffer.isView(payload)) {
    const view = payload as ArrayBufferView;
    return new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
  }
  return undefined;
}

// --- Transport Stream SCTE-35 parsing ---

export function detectTsPacketFormat(
  data: Uint8Array
): { offset: number; packetSize: number; tsPayloadOffset: number } | undefined {
  const packetSizes = [188, 192, 204];
  for (const packetSize of packetSizes) {
    const tsPayloadOffset = packetSize === 192 ? 4 : 0;
    for (let offset = 0; offset < packetSize; offset++) {
      let matchedPackets = 0;
      for (
        let packetStart = offset;
        packetStart + tsPayloadOffset < data.length &&
        matchedPackets < 5;
        packetStart += packetSize
      ) {
        if (data[packetStart + tsPayloadOffset] !== 0x47) {
          break;
        }
        matchedPackets++;
      }
      if (matchedPackets >= 2) {
        return { offset, packetSize, tsPayloadOffset };
      }
    }
  }
  return undefined;
}

function getPsiSectionStart(
  payload: Uint8Array,
  payloadUnitStart: boolean
): number | undefined {
  if (!payloadUnitStart) {
    return undefined;
  }
  const pointerField = payload[0];
  if (pointerField == null) {
    return undefined;
  }
  const sectionStart = 1 + pointerField;
  return sectionStart < payload.length ? sectionStart : undefined;
}

function getPsiSectionLength(data: Uint8Array, offset: number): number {
  return (((data[offset + 1] ?? 0) & 0x0f) << 8) | (data[offset + 2] ?? 0);
}

function parsePatSection(
  payload: Uint8Array,
  payloadUnitStart: boolean,
  pmtPids: Set<number>
): void {
  const sectionStart = getPsiSectionStart(payload, payloadUnitStart);
  if (sectionStart == null || sectionStart + 8 >= payload.length) {
    return;
  }
  if (payload[sectionStart] !== 0x00) {
    return;
  }

  const sectionLength = getPsiSectionLength(payload, sectionStart);
  const sectionEnd = Math.min(
    payload.length,
    sectionStart + 3 + sectionLength
  );

  for (let offset = sectionStart + 8; offset + 4 <= sectionEnd - 4; offset += 4) {
    const programNumber =
      ((payload[offset] ?? 0) << 8) | (payload[offset + 1] ?? 0);
    const pid =
      (((payload[offset + 2] ?? 0) & 0x1f) << 8) |
      (payload[offset + 3] ?? 0);
    if (programNumber !== 0) {
      pmtPids.add(pid);
    }
  }
}

function descriptorsIdentifyScte35(
  data: Uint8Array,
  start: number,
  end: number
): boolean {
  let offset = start;
  while (offset + 2 <= end) {
    const tag = data[offset] ?? 0;
    const length = data[offset + 1] ?? 0;
    const descriptorStart = offset + 2;
    const descriptorEnd = descriptorStart + length;
    if (descriptorEnd > end) {
      return false;
    }

    const registration =
      length >= 4
        ? String.fromCharCode(
            data[descriptorStart] ?? 0,
            data[descriptorStart + 1] ?? 0,
            data[descriptorStart + 2] ?? 0,
            data[descriptorStart + 3] ?? 0
          )
        : "";
    if (tag === 0x8a || registration === "CUEI" || registration === "SCTE") {
      return true;
    }

    offset = descriptorEnd;
  }
  return false;
}

function parsePmtSection(
  payload: Uint8Array,
  payloadUnitStart: boolean,
  tsScte35Pids: Set<number>
): void {
  const sectionStart = getPsiSectionStart(payload, payloadUnitStart);
  if (sectionStart == null || sectionStart + 12 >= payload.length) {
    return;
  }
  if (payload[sectionStart] !== 0x02) {
    return;
  }

  const sectionLength = getPsiSectionLength(payload, sectionStart);
  const sectionEnd = Math.min(
    payload.length,
    sectionStart + 3 + sectionLength
  );
  const programInfoLength =
    (((payload[sectionStart + 10] ?? 0) & 0x0f) << 8) |
    (payload[sectionStart + 11] ?? 0);

  let offset = sectionStart + 12 + programInfoLength;
  while (offset + 5 <= sectionEnd - 4) {
    const streamType = payload[offset] ?? 0;
    const elementaryPid =
      (((payload[offset + 1] ?? 0) & 0x1f) << 8) |
      (payload[offset + 2] ?? 0);
    const esInfoLength =
      (((payload[offset + 3] ?? 0) & 0x0f) << 8) |
      (payload[offset + 4] ?? 0);
    const descriptorStart = offset + 5;
    const descriptorEnd = Math.min(descriptorStart + esInfoLength, sectionEnd - 4);

    if (
      streamType === 0x86 ||
      (streamType === 0x06 &&
        descriptorsIdentifyScte35(payload, descriptorStart, descriptorEnd))
    ) {
      tsScte35Pids.add(elementaryPid);
    }

    offset = descriptorStart + esInfoLength;
  }
}

function hasProcessedTsScte35Section(
  section: Uint8Array,
  processedSections: Set<string>
): boolean {
  let checksum = 0;
  for (let i = 0; i < section.length; i++) {
    checksum = (checksum * 31 + (section[i] ?? 0)) >>> 0;
  }

  const key = `${section.length}:${checksum}`;
  if (processedSections.has(key)) {
    return true;
  }

  processedSections.add(key);
  if (processedSections.size > 200) {
    const firstKey = processedSections.values().next().value;
    if (firstKey) {
      processedSections.delete(firstKey);
    }
  }
  return false;
}

function addScte35SectionMarker(
  section: Uint8Array,
  markers: Scte35Marker[],
  processedSections: Set<string>
): void {
  if (section[0] !== 0xfc || hasProcessedTsScte35Section(section, processedSections)) {
    return;
  }

  const marker = parseScte35Binary(section);
  if (!marker) {
    return;
  }

  marker.raw = {
    ...(typeof marker.raw === "object" && marker.raw ? marker.raw : {}),
    source: "ts-packet",
  };
  markers.push(marker);
}

function appendScte35Bytes(
  pid: number,
  bytes: Uint8Array,
  assemblers: Map<number, { bytes: number[]; expectedLength: number }>,
  markers: Scte35Marker[],
  processedSections: Set<string>
): void {
  const assembler = assemblers.get(pid);
  if (!assembler) {
    return;
  }

  for (let i = 0; i < bytes.length && assembler.bytes.length < assembler.expectedLength; i++) {
    assembler.bytes.push(bytes[i] ?? 0);
  }

  if (assembler.bytes.length >= assembler.expectedLength) {
    assemblers.delete(pid);
    addScte35SectionMarker(new Uint8Array(assembler.bytes), markers, processedSections);
  }
}

function collectScte35SectionsFromPayload(
  pid: number,
  payload: Uint8Array,
  payloadUnitStart: boolean,
  assemblers: Map<number, { bytes: number[]; expectedLength: number }>,
  markers: Scte35Marker[],
  processedSections: Set<string>
): void {
  if (payload.length === 0) {
    return;
  }

  let offset = 0;
  if (payloadUnitStart) {
    const pointerField = payload[0] ?? 0;
    const previousAssembler = assemblers.get(pid);
    if (previousAssembler && pointerField > 0) {
      appendScte35Bytes(
        pid,
        payload.subarray(1, Math.min(1 + pointerField, payload.length)),
        assemblers,
        markers,
        processedSections
      );
    }
    assemblers.delete(pid);
    offset = 1 + pointerField;
  } else {
    appendScte35Bytes(pid, payload, assemblers, markers, processedSections);
    return;
  }

  while (offset + 3 <= payload.length) {
    const tableId = payload[offset];
    if (tableId == null || tableId === 0xff) {
      return;
    }

    const sectionLength = getPsiSectionLength(payload, offset);
    const totalLength = 3 + sectionLength;
    if (sectionLength <= 0 || totalLength <= 3) {
      return;
    }

    if (offset + totalLength <= payload.length) {
      addScte35SectionMarker(
        payload.subarray(offset, offset + totalLength),
        markers,
        processedSections
      );
      offset += totalLength;
    } else {
      assemblers.set(pid, {
        bytes: Array.from(payload.subarray(offset)),
        expectedLength: totalLength,
      });
      return;
    }
  }
}

export function parseScte35FromTsPackets(
  data: Uint8Array,
  tsScte35Pids: Set<number>,
  pmtPids: Set<number>,
  processedSections: Set<string>
): Scte35Marker[] {
  const packetInfo = detectTsPacketFormat(data);
  if (!packetInfo) {
    return [];
  }

  const markers: Scte35Marker[] = [];
  const sectionAssemblers = new Map<
    number,
    { bytes: number[]; expectedLength: number }
  >();

  for (
    let packetStart = packetInfo.offset;
    packetStart + packetInfo.packetSize <= data.length;
    packetStart += packetInfo.packetSize
  ) {
    const tsStart = packetStart + packetInfo.tsPayloadOffset;
    if (data[tsStart] !== 0x47) {
      continue;
    }

    const secondByte = data[tsStart + 1];
    const thirdByte = data[tsStart + 2];
    const fourthByte = data[tsStart + 3];
    if (
      secondByte == null ||
      thirdByte == null ||
      fourthByte == null ||
      (secondByte & 0x80) !== 0
    ) {
      continue;
    }

    const payloadUnitStart = (secondByte & 0x40) !== 0;
    const pid = ((secondByte & 0x1f) << 8) | thirdByte;
    const adaptationFieldControl = (fourthByte >> 4) & 0x03;
    const hasPayload =
      adaptationFieldControl === 1 || adaptationFieldControl === 3;
    if (!hasPayload) {
      continue;
    }

    let payloadStart = tsStart + 4;
    if (adaptationFieldControl === 3) {
      const adaptationLength = data[payloadStart];
      if (adaptationLength == null) {
        continue;
      }
      payloadStart += 1 + adaptationLength;
    }

    const payloadEnd = Math.min(tsStart + 188, data.length);
    if (payloadStart >= payloadEnd || payloadStart >= data.length) {
      continue;
    }

    const payload = data.subarray(payloadStart, payloadEnd);
    if (pid === 0) {
      parsePatSection(payload, payloadUnitStart, pmtPids);
    } else if (pmtPids.has(pid)) {
      parsePmtSection(payload, payloadUnitStart, tsScte35Pids);
    } else if (tsScte35Pids.has(pid)) {
      collectScte35SectionsFromPayload(
        pid,
        payload,
        payloadUnitStart,
        sectionAssemblers,
        markers,
        processedSections
      );
    }
  }

  return markers;
}

// --- Binary SCTE-35 parsing ---

class BitReader {
  private bytePos = 0;
  private bitPos = 0;
  constructor(private readonly buf: Uint8Array) {}
  readBits(numBits: number): number {
    let result = 0;
    while (numBits > 0) {
      if (this.bytePos >= this.buf.length) return result;
      const remainingInByte = 8 - this.bitPos;
      const toRead = Math.min(numBits, remainingInByte);
      const currentByte = this.buf[this.bytePos]!;
      const shift = remainingInByte - toRead;
      const mask = ((1 << toRead) - 1) & 0xff;
      const bits = (currentByte >> shift) & mask;
      result = (result << toRead) | bits;
      this.bitPos += toRead;
      if (this.bitPos >= 8) {
        this.bitPos = 0;
        this.bytePos += 1;
      }
      numBits -= toRead;
    }
    return result >>> 0;
  }
  skipBits(n: number): void {
    this.readBits(n);
  }
  get bytePosition(): number {
    return this.bitPos === 0 ? this.bytePos : this.bytePos + 1;
  }
}

function readSpliceTime(
  r: BitReader,
  ptsAdjustmentTicks: number
): number | undefined {
  const timeSpecifiedFlag = r.readBits(1) === 1;
  if (!timeSpecifiedFlag) {
    r.readBits(7);
    return undefined;
  }
  r.readBits(6);
  const high = r.readBits(1);
  const low = r.readBits(32);
  const ptsTicks =
    (high * 0x100000000 + low + ptsAdjustmentTicks) % 0x200000000;
  return ptsTicks / 90000;
}

function markerTypeFromSegmentationTypeId(
  segmentationTypeId: number
): Scte35Marker["type"] | undefined {
  switch (segmentationTypeId) {
    case 0x10:
    case 0x22:
    case 0x30:
    case 0x32:
    case 0x34:
    case 0x36:
      return "start";
    case 0x11:
    case 0x23:
    case 0x31:
    case 0x33:
    case 0x35:
    case 0x37:
      return "end";
    default:
      return undefined;
  }
}

function parseScte35SegmentationDescriptor(
  descriptor: Uint8Array
):
  | {
      type: Scte35Marker["type"];
      durationSeconds?: number;
      segmentationEventId?: number;
    }
  | undefined {
  if (descriptor.length < 11) {
    return undefined;
  }

  const identifier = String.fromCharCode(
    descriptor[0] ?? 0,
    descriptor[1] ?? 0,
    descriptor[2] ?? 0,
    descriptor[3] ?? 0
  );
  if (identifier !== "CUEI") {
    return undefined;
  }

  const segmentationEventId =
    ((descriptor[4] ?? 0) * 0x1000000) +
    ((descriptor[5] ?? 0) << 16) +
    ((descriptor[6] ?? 0) << 8) +
    (descriptor[7] ?? 0);
  const cancelIndicator = ((descriptor[8] ?? 0) & 0x80) !== 0;
  if (cancelIndicator) {
    return undefined;
  }

  const flags = descriptor[9] ?? 0;
  const programSegmentationFlag = (flags & 0x80) !== 0;
  const segmentationDurationFlag = (flags & 0x40) !== 0;
  let offset = 10;

  if (!programSegmentationFlag) {
    const componentCount = descriptor[offset] ?? 0;
    offset += 1 + componentCount * 6;
    if (offset > descriptor.length) {
      return undefined;
    }
  }

  let durationSeconds: number | undefined = undefined;
  if (segmentationDurationFlag) {
    if (offset + 5 > descriptor.length) {
      return undefined;
    }
    const durationTicks =
      ((descriptor[offset] ?? 0) * 0x100000000) +
      ((descriptor[offset + 1] ?? 0) * 0x1000000) +
      ((descriptor[offset + 2] ?? 0) << 16) +
      ((descriptor[offset + 3] ?? 0) << 8) +
      (descriptor[offset + 4] ?? 0);
    durationSeconds = durationTicks / 90000;
    offset += 5;
  }

  if (offset + 2 > descriptor.length) {
    return undefined;
  }
  offset += 1;
  const segmentationUpidLength = descriptor[offset] ?? 0;
  offset += 1 + segmentationUpidLength;
  if (offset >= descriptor.length) {
    return undefined;
  }

  const segmentationTypeId = descriptor[offset] ?? 0;
  const markerType = markerTypeFromSegmentationTypeId(segmentationTypeId);
  if (!markerType) {
    return undefined;
  }

  return {
    type: markerType,
    ...(durationSeconds !== undefined ? { durationSeconds } : {}),
    segmentationEventId,
  };
}

function parseScte35SegmentationDescriptors(
  data: Uint8Array,
  offset: number
):
  | {
      type: Scte35Marker["type"];
      durationSeconds?: number;
      segmentationEventId?: number;
    }
  | undefined {
  if (offset + 2 > data.length) {
    return undefined;
  }

  const descriptorLoopLength =
    ((data[offset] ?? 0) << 8) | (data[offset + 1] ?? 0);
  let descriptorOffset = offset + 2;
  const descriptorEnd = Math.min(
    data.length,
    descriptorOffset + descriptorLoopLength
  );

  while (descriptorOffset + 2 <= descriptorEnd) {
    const descriptorTag = data[descriptorOffset] ?? 0;
    const descriptorLength = data[descriptorOffset + 1] ?? 0;
    const descriptorDataStart = descriptorOffset + 2;
    const descriptorDataEnd = descriptorDataStart + descriptorLength;
    if (descriptorDataEnd > descriptorEnd) {
      return undefined;
    }

    if (descriptorTag === 0x02) {
      const descriptorMarker = parseScte35SegmentationDescriptor(
        data.subarray(descriptorDataStart, descriptorDataEnd)
      );
      if (descriptorMarker) {
        return descriptorMarker;
      }
    }

    descriptorOffset = descriptorDataEnd;
  }

  return undefined;
}

export function parseScte35Binary(data: Uint8Array): Scte35Marker | undefined {
  const r = new BitReader(data);
  const tableId = r.readBits(8);
  if (tableId !== 0xfc) return undefined;
  r.readBits(1);
  r.readBits(1);
  r.readBits(2);
  const sectionLength = r.readBits(12);
  if (sectionLength <= 0 || data.length < 3 + sectionLength) {
    return undefined;
  }
  r.readBits(8);
  r.readBits(1);
  r.readBits(6);
  const ptsAdjHigh = r.readBits(1);
  const ptsAdjLow = r.readBits(32);
  const ptsAdjustmentTicks = ptsAdjHigh * 0x100000000 + ptsAdjLow;
  r.readBits(8);
  r.readBits(12);
  const spliceCommandLength = r.readBits(12);
  const spliceCommandType = r.readBits(8);

  let markerType: Scte35Marker["type"] | undefined;
  let durationSeconds: number | undefined = undefined;
  let ptsSeconds: number | undefined = undefined;
  let spliceEventId: number | undefined = undefined;
  const commandBodyStart = r.bytePosition;

  if (spliceCommandType === 5) {
    spliceEventId = r.readBits(32);
    const cancel = r.readBits(1) === 1;
    r.readBits(7);
    if (cancel) return undefined;
    const outOfNetwork = r.readBits(1) === 1;
    const programSpliceFlag = r.readBits(1) === 1;
    const durationFlag = r.readBits(1) === 1;
    const spliceImmediateFlag = r.readBits(1) === 1;
    r.readBits(4);
    markerType = outOfNetwork ? "start" : "end";

    if (programSpliceFlag && !spliceImmediateFlag) {
      ptsSeconds = readSpliceTime(r, ptsAdjustmentTicks);
    } else if (!programSpliceFlag) {
      const componentCount = r.readBits(8);
      for (let i = 0; i < componentCount; i++) {
        r.readBits(8);
        if (!spliceImmediateFlag) {
          const componentPtsSeconds = readSpliceTime(r, ptsAdjustmentTicks);
          if (ptsSeconds === undefined) {
            ptsSeconds = componentPtsSeconds;
          }
        }
      }
    }

    if (durationFlag) {
      r.readBits(6);
      r.readBits(1);
      const high = r.readBits(1);
      const low = r.readBits(32);
      const durationTicks = high * 0x100000000 + low;
      durationSeconds = durationTicks / 90000;
    }
    r.readBits(16);
    r.readBits(8);
    r.readBits(8);
  } else if (spliceCommandType === 6) {
    ptsSeconds = readSpliceTime(r, ptsAdjustmentTicks);
  } else {
    return undefined;
  }

  const expectedDescriptorOffset =
    spliceCommandLength === 0x0fff
      ? r.bytePosition
      : commandBodyStart + spliceCommandLength;
  const descriptorMarker = parseScte35SegmentationDescriptors(
    data,
    expectedDescriptorOffset
  );

  if (descriptorMarker) {
    markerType = descriptorMarker.type;
    if (durationSeconds === undefined) {
      durationSeconds = descriptorMarker.durationSeconds;
    }
  }

  if (!markerType) {
    return undefined;
  }

  const marker: Scte35Marker = {
    type: markerType,
    ...(ptsSeconds !== undefined ? { ptsSeconds } : {}),
    ...(durationSeconds !== undefined ? { durationSeconds } : {}),
    raw: {
      splice_command_type: spliceCommandType,
      ...(spliceEventId !== undefined ? { splice_event_id: spliceEventId } : {}),
      ...(descriptorMarker?.segmentationEventId !== undefined
        ? { segmentation_event_id: descriptorMarker.segmentationEventId }
        : {}),
    },
  } as Scte35Marker;
  return marker;
}
